import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BigButton } from "../components/BigButton";
import {
  clearCredentials,
  getDeviceId,
  getLocationId,
  getReKeyRequiredAt,
  refreshAccessToken,
} from "../lib/device-auth";
import { KioskApiError, kioskClient } from "../lib/kiosk-client";

interface Props {
  onClose: () => void;
  onRevoke: () => void;
}

export function AdminPage({ onClose, onRevoke }: Props) {
  const { t } = useTranslation();
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");

  async function handlePinSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pin || submitting) return;
    setSubmitting(true);
    setPinError(null);
    try {
      await kioskClient.adminUnlock(pin);
      setUnlocked(true);
    } catch (err) {
      setPin("");
      if (err instanceof KioskApiError && err.status === 423) {
        setPinError(t("admin.pinNotConfigured"));
      } else if (err instanceof KioskApiError && err.status === 401) {
        setPinError(t("admin.pinIncorrect"));
      } else {
        setPinError(t("admin.unlockError"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForceRefresh() {
    setRefreshing(true);
    setRefreshMsg("");
    try {
      await refreshAccessToken();
      setRefreshMsg("Token refreshed successfully.");
    } catch {
      setRefreshMsg("Refresh failed. Device may need re-provisioning.");
    } finally {
      setRefreshing(false);
    }
  }

  function handleRevoke() {
    clearCredentials();
    onRevoke();
  }

  if (!unlocked) {
    return (
      <div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
        data-testid="admin-page"
      >
        <div className="bg-[var(--kiosk-brand-surface)] rounded-[var(--kiosk-brand-radius)] p-10 w-96 flex flex-col gap-6">
          <h2 className="text-2xl font-bold text-center">{t("admin.title")}</h2>
          <form
            onSubmit={(e) => void handlePinSubmit(e)}
            className="flex flex-col gap-4"
            noValidate
          >
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                setPinError(null);
              }}
              placeholder={t("admin.pinPrompt")}
              className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-xl text-center tracking-widest"
              autoFocus
              maxLength={8}
            />
            {pinError && (
              <p role="alert" className="text-red-600 text-center">
                {pinError}
              </p>
            )}
            <BigButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={!pin || submitting}
            >
              {submitting ? t("admin.pinChecking") : t("admin.pinSubmit")}
            </BigButton>
          </form>
          <BigButton variant="secondary" fullWidth onClick={onClose}>
            {t("admin.close")}
          </BigButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      data-testid="admin-page-unlocked"
    >
      <div className="bg-[var(--kiosk-brand-surface)] rounded-[var(--kiosk-brand-radius)] p-10 w-[480px] flex flex-col gap-6">
        <h2 className="text-2xl font-bold text-center">{t("admin.title")}</h2>
        <dl className="flex flex-col gap-2 text-base">
          <div className="flex justify-between gap-4">
            <dt className="font-medium text-[var(--kiosk-brand-muted)]">
              {t("admin.deviceId")}
            </dt>
            <dd className="font-mono text-xs truncate">
              {getDeviceId() ?? "—"}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="font-medium text-[var(--kiosk-brand-muted)]">
              {t("admin.locationId")}
            </dt>
            <dd className="font-mono text-xs truncate">
              {getLocationId() ?? "—"}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="font-medium text-[var(--kiosk-brand-muted)]">
              {t("admin.reKeyAt")}
            </dt>
            <dd className="text-sm">{getReKeyRequiredAt() ?? "—"}</dd>
          </div>
        </dl>
        {refreshMsg && (
          <p className="text-sm text-center text-[var(--kiosk-brand-muted)]">
            {refreshMsg}
          </p>
        )}
        <BigButton
          variant="secondary"
          fullWidth
          onClick={() => void handleForceRefresh()}
          disabled={refreshing}
        >
          {t("admin.forceRefresh")}
        </BigButton>
        <BigButton variant="danger" fullWidth onClick={handleRevoke}>
          {t("admin.revoke")}
        </BigButton>
        <BigButton variant="secondary" fullWidth onClick={onClose}>
          {t("admin.close")}
        </BigButton>
      </div>
    </div>
  );
}
